import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Package,
  Tag,
  ArrowLeft,
  Archive,
  Percent,
  Calendar,
} from "lucide-react";
import { getProductById } from "../../../services/productsApi";
import { useAuth } from "../../../auth/AuthContext";
import { Product } from "../../../types/product";
import GlobalLoader from "../../../components/ui/GlobalLoader";
import PageHeader from "../../../components/ui/PageHeader";
import { cn } from "../../../lib/utils";

export default function ProductDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const currencySymbol = user?.company?.currency_symbol || "";
  const currencyCode = user?.company?.currency_code || "GBP";

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await getProductById(id);
        if (data) {
          setProduct(data);
        } else {
          setError("Product not found.");
        }
      } catch (err) {
        console.error("Failed to load product details", err);
        setError("Could not load product details.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="flex h-[80vh] w-full items-center justify-center bg-gray-50">
        <GlobalLoader description="Loading inventory details..." />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-50 text-center p-6">
        <Package className="w-12 h-12 text-[#885ABB]/50 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Item Not Found
        </h2>
        <p className="text-gray-500 mb-6">
          {error || "The requested product details could not be found."}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-[#885ABB] text-white rounded-full hover:bg-[#724b9e] transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number | string) => {
    const val = typeof amount === "string" ? parseFloat(amount) : amount;
    try {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: currencyCode,
      }).format(val || 0);
    } catch (e) {
      return `${currencySymbol}${Number(val || 0).toFixed(2)}`;
    }
  };

  const showDiscount = Boolean(product.discount) && parseFloat(String(product.discount)) > 0;

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <PageHeader
        title="Product Details"
        icon={Package}
        className="border-b-2 border-gray-300 shadow-sm"
        actions={
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Hero Section */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50 rounded-full blur-3xl -mr-32 -mt-32 opacity-50"></div>

          <div className="relative flex flex-col md:flex-row items-center md:items-start gap-8">
            <div className="relative">
              <div className="w-32 h-32 rounded-full bg-[#885ABB]/10 border-4 border-white shadow-md flex items-center justify-center overflow-hidden">
                {product.image &&
                  !product.image.includes("default") &&
                  !product.image.includes("placeholder") ? (
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Package className="w-16 h-16 text-[#885ABB]/70" />
                )}
              </div>
            </div>

            <div className="flex-1 text-center md:text-left space-y-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center justify-center md:justify-start gap-3">
                  {product.name}
                </h1>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-2">
                  <p className="text-gray-500 flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-[#885ABB]" />
                    Created on {formatDate(product.createdAt)}
                  </p>
                </div>
              </div>

              {showDiscount && (
                <div className="flex flex-wrap justify-center md:justify-start gap-2">
                  <span className="px-3 py-1 rounded-full bg-red-50 text-red-600 text-[10px] font-bold uppercase border border-red-100 flex items-center gap-1">
                    <Percent className="w-3 h-3" /> {product.discount}% Discount
                  </span>
                </div>
              )}
            </div>

            {/* Quick Stats Grid */}
            <div className="flex flex-wrap gap-4 w-full md:w-auto">
              {/* Retail Price Box */}
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 text-center min-w-[140px] transition-all hover:shadow-md">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Retail Price
                </p>
                <p className="text-xl font-extrabold text-gray-900 mt-1">
                  {formatCurrency(product.price || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {product.taxes && product.taxes.length > 0 && (
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b-2 border-purple-200 bg-white">
                  <h3 className="font-semibold text-[#885ABB]">
                    Applicable Taxes
                  </h3>
                </div>
                <div className="p-6 space-y-3">
                  {product.taxes.map((tax, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        <Percent size={14} className="text-[#885ABB]" />
                        <span className="text-sm font-medium text-gray-700">
                          {tax.name}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-gray-900">
                        {tax.percent}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className={`${product.taxes && product.taxes.length > 0 ? "lg:col-span-3" : "lg:col-span-4"} space-y-6`}>
            <div className="border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b-2 border-[#885ABB] inline-block">
                Product Overview
              </h3>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-8 space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  Description
                </h4>
                <p className="text-gray-700 leading-relaxed break-words whitespace-pre-wrap">
                  {product.description ||
                    "No detailed description available for this product."}
                </p>
              </div>

              <div className="grid grid-cols-1 pt-6 border-t border-gray-100">
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Pricing Details
                  </h4>
                  <div className="max-w-md space-y-2">
                    {/* Net Price */}
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <span className="text-gray-500 text-sm">Net Price (Excl. Tax):</span>
                      <div className="text-right">
                        <span className={cn("font-bold text-lg", product.discountedPrice && product.discountedPrice < product.price && "line-through text-gray-400 text-sm block")}>
                          {formatCurrency(product.price || 0)}
                        </span>
                        {product.discountedPrice && product.discountedPrice < product.price && (
                          <span className="font-bold text-lg text-[#885ABB]">
                            {formatCurrency(product.discountedPrice)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Tax Amount */}
                    {(() => {
                      const totalTaxPercent = product.taxes?.reduce((sum, tax) => sum + tax.percent, 0) || 0;
                      const activePrice = (product.discountedPrice && product.discountedPrice < product.price) ? product.discountedPrice : product.price;
                      const taxAmount = activePrice * (totalTaxPercent / 100);

                      return (
                        <div className="flex justify-between items-start p-3 bg-gray-50 rounded-lg border border-gray-100">
                          <div className="flex flex-col gap-1">
                            <span className="text-gray-500 text-sm">Tax (GST/VAT):</span>
                            {product.taxes && product.taxes.length > 0 && (
                              <div className="flex flex-col gap-0.5 mt-1">
                                {product.taxes.map((t, idx) => (
                                  <span key={idx} className="text-[10px] text-gray-400">
                                    {t.name}: {formatCurrency(activePrice * (t.percent / 100))} ({t.percent}%)
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <span className="font-bold text-lg">
                            {formatCurrency(taxAmount)}
                          </span>
                        </div>
                      );
                    })()}

                    {/* Gross Price */}
                    {(() => {
                      const totalTaxPercent = product.taxes?.reduce((sum, tax) => sum + tax.percent, 0) || 0;
                      const originalGross = product.price * (1 + totalTaxPercent / 100);
                      const discountedGross = product.discountedPrice ? product.discountedPrice * (1 + totalTaxPercent / 100) : null;

                      return (
                        <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg border border-purple-100 mt-2">
                          <span className="text-[#885ABB] text-sm font-bold">Total (Inc. Tax):</span>
                          <div className="text-right">
                            <span className={cn("font-bold text-xl text-gray-900", discountedGross && discountedGross < originalGross && "line-through text-gray-400 text-sm block")}>
                              {formatCurrency(originalGross)}
                            </span>
                            {discountedGross && discountedGross < originalGross && (
                              <span className="font-extrabold text-2xl text-[#885ABB]">
                                {formatCurrency(discountedGross)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
              <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-6">
                Product Identification & Category
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest mb-1">
                    SKU
                  </p>
                  <p className="text-sm font-mono font-bold text-gray-700 break-all">
                    {product.sku || "N/A"}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest mb-1">
                    Barcode
                  </p>
                  <p className="text-sm font-mono font-bold text-gray-700 break-all">
                    {product.barcode || "N/A"}
                  </p>
                </div>
                {/* <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest mb-1">
                    Category
                  </p>
                  <p className="text-sm font-bold text-gray-700">
                    {product.category || "N/A"}
                  </p>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
